"use client";

import React, { useState, useEffect, useRef, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import logo from "/public/images/header/white_logo.svg";
import profile from "/public/images/header/profile.png";
import NavLink from "./NavLink";
import useAuthStore, { rehydrateAuth } from "@/stores/useAuthStore";
import { IoCaretDown, IoCaretUp } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";
import { FiArrowRight, FiMenu, FiX, FiChevronRight } from "react-icons/fi";
import { useLogout } from "@/hooks/useAuth";
import { useRouter } from "next/navigation";
import { set } from "zod";

function Navbar() {
  const router = useRouter();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const { user, refreshToken, setAuth, setUser, clearAuth } = useAuthStore();

  const logoutMutation = useLogout();

  // ✅ Handle resize close mobile nav
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Rehydrate auth from storage on client side (avoids SSR/CSR hydration mismatch)
  useEffect(() => {
    rehydrateAuth();
  }, []);

  // ✅ Handle click outside dropdown
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Handle logout
  const handleLogout = () => {
    logoutMutation.mutate(
      { refreshToken },
      {
        onSuccess: () => {
          // clear auth from store + storages
          clearAuth();
          setDropdownOpen(false);
          console.log("✅ Logged out successfully");
          // ✅ Redirect client-side
          router.push("/"); // or "/"
        },
        onError: (err) => {
          console.error("❌ Logout failed:", err.response?.data || err.message);
        },
      },
    );
  };

  // compute first-two initials for mobile avatar
  const userInitials = useMemo(() => {
    const name = user?.fullName || user?.name || "";
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (
      String(parts[0][0] || "") + String(parts[1][0] || "")
    ).toUpperCase();
  }, [user?.fullName, user?.name]);

  return (
    <>
      {/* Top Navbar */}
      <nav className="navbar-glass w-full text-white z-50 sticky top-0 flex flex-col justify-center items-center px-[25px] lg:px-3 min-h-[83px]">
        <div className="w-full max-w-[1338px] h-14 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2">
            <Image
              src={logo}
              alt="Prop Radar Logo"
              width={40}
              height={56}
              priority
            />
            <span className="text-white text-[22px] leading-none tracking-tight">
              Prop Radar
            </span>
          </Link>

          {/* Desktop Menu */}
          <ul className="hidden md:flex items-center gap-8">
            <li>
              <NavLink
                href="/"
                exact
                className="text-[16.6px] leading-6 hover:text-themeColor"
                activeClassName="text-themeColor font-bold"
              >
                Home
              </NavLink>
            </li>
            <li>
              <NavLink
                href="/compare"
                className="text-[16.6px] leading-6 hover:text-themeColor"
                activeClassName="text-themeColor font-bold"
              >
                Compare
              </NavLink>
            </li>
            <li>
              <NavLink
                href="/firm-listing"
                className="text-[16.6px] leading-6 hover:text-themeColor"
                activeClassName="text-themeColor font-bold"
              >
                PropFirm
              </NavLink>
            </li>
            <li>
              <NavLink
                href="/discount"
                className="text-[16.6px] leading-6 hover:text-themeColor"
                activeClassName="text-themeColor font-bold"
              >
                Discount
              </NavLink>
            </li>
            {/* <li>
              <NavLink href="/contact" className="text-[16.6px] leading-6 hover:text-themeColor" activeClassName="text-themeColor font-bold">
                Contact
              </NavLink>
            </li> */}
            <li>
              <NavLink
                href="/reviews"
                className="text-[16.6px] leading-6 hover:text-themeColor"
                activeClassName="text-themeColor font-bold"
              >
                Reviews
              </NavLink>
            </li>
            <li>
              <NavLink
                href="/blog"
                className="text-[16.6px] leading-6 hover:text-themeColor"
                activeClassName="text-themeColor font-bold"
              >
                Blogs
              </NavLink>
            </li>

            {/* Auth Buttons */}
            <li className="relative" ref={dropdownRef}>
              {!user ? (
                <Link href="/auth">
                  <button className="box-border inline-flex items-center justify-center gap-[14px] px-[19px] py-3 min-w-[168px] h-[43px] text-white text-[16.6px] leading-6 font-normal border-[2.4px] border-themeColor rounded-[11px] bg-transparent hover:bg-themeColor/10 transition-colors">
                    Get Started
                    <FiArrowRight className="text-[18px] shrink-0" />
                  </button>
                </Link>
              ) : (
                <div
                  className="flex items-center gap-2 cursor-pointer select-none"
                  onClick={() => setDropdownOpen((prev) => !prev)}
                >
                  {user?.avatarUrl ? (
                    <Image
                      src={user.avatarUrl}
                      alt={
                        user?.fullName
                          ? `${user.fullName} avatar`
                          : "Profile avatar"
                      }
                      width={36}
                      height={36}
                      className="rounded-full object-cover shrink-0"
                      priority
                    />
                  ) : (
                    <Image
                      src={profile}
                      alt=""
                      width={36}
                      height={36}
                      priority
                    />
                  )}
                  <span>{user?.fullName}</span>
                  {dropdownOpen ? <IoCaretUp /> : <IoCaretDown />}
                </div>
              )}

              {/* Dropdown */}
              <AnimatePresence>
                {dropdownOpen && user && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-3 w-48 rounded-xl shadow-lg bg-[#000] border border-themeColor text-white overflow-hidden py-4"
                  >
                    <div className="flex flex-col">
                      <Link
                        href="/dashboard/profile"
                        className="px-4 py-2 text-left hover:bg-themeColor hover:text-black transition"
                        onClick={() => setDropdownOpen(false)}
                      >
                        Profile
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="px-4 py-2 text-left hover:bg-themeColor hover:text-black transition"
                      >
                        Logout
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </li>
          </ul>

          {/* Mobile Hamburger Icon */}
          <div className="md:hidden flex items-center gap-4">
            {/* Mobile: User profile quick card (visible when logged in) */}
            {user && (
              <Link
                href="/dashboard/profile"
                onClick={() => setMobileOpen(false)}
                aria-label={`Open profile for ${user?.fullName || "profile"}`}
                className="inline-flex items-center"
              >
                <span className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold bg-themeColor overflow-hidden shrink-0">
                  {user?.avatarUrl ? (
                    <Image
                      src={user.avatarUrl}
                      alt={
                        user?.fullName
                          ? `${user.fullName} avatar`
                          : "Profile avatar"
                      }
                      width={40}
                      height={40}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    userInitials
                  )}
                </span>
              </Link>
            )}
            <button
              onClick={() => setMobileOpen(true)}
              className="text-white"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
            >
              <FiMenu size={28} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Full-Screen Overlay */}
      <div
        className={`fixed inset-0 bg-black/85 backdrop-blur-sm z-[999] transform transition-transform duration-300 ease-in-out 
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"} 
          flex flex-col justify-start text-white px-6 pt-6 md:hidden`}
      >
        {/* Top: Logo + Close */}
        <div className="flex items-center justify-between mb-10">
          <Link
            href="/"
            className="flex items-center gap-2"
            onClick={() => setMobileOpen(false)}
          >
            <Image src={logo} alt="PropRadar Logo" width={36} height={36} />
            <span className="text-xl font-bold">Prop Radar</span>
          </Link>
          <button onClick={() => setMobileOpen(false)} className="text-white">
            <FiX size={28} />
          </button>
        </div>

        {/* Nav Links */}
        <nav className="flex flex-col gap-8 text-[17px] font-medium">
          <NavLink
            href="/"
            onClick={() => setMobileOpen(false)}
            className="text-lg hover:text-gradientStart flex justify-between items-center"
            activeClassName="text-gradientStart"
          >
            Home
          </NavLink>
          <NavLink
            href="/compare"
            onClick={() => setMobileOpen(false)}
            className="text-lg hover:text-gradientStart flex justify-between items-center"
            activeClassName="text-gradientStart"
          >
            Compare <FiChevronRight size={18} />
          </NavLink>
          <NavLink
            href="/firm-listing"
            onClick={() => setMobileOpen(false)}
            className="text-lg hover:text-gradientStart flex justify-between items-center"
            activeClassName="text-gradientStart"
          >
            PropFirm <FiChevronRight size={18} />
          </NavLink>
          <NavLink
            href="/discount"
            onClick={() => setMobileOpen(false)}
            className="text-lg hover:text-gradientStart flex justify-between items-center"
            activeClassName="text-gradientStart"
          >
            Discount <FiChevronRight size={18} />
          </NavLink>
          {/* <NavLink href="/contact" onClick={() => setMobileOpen(false)} className="text-lg hover:text-gradientStart flex justify-between items-center" activeClassName="text-gradientStart">
            Contact <FiChevronRight size={18} />
          </NavLink> */}
          <NavLink
            href="/reviews"
            onClick={() => setMobileOpen(false)}
            className="text-lg hover:text-gradientStart flex justify-between items-center"
            activeClassName="text-gradientStart"
          >
            Reviews <FiChevronRight size={18} />
          </NavLink>
          <NavLink
            href="/blog"
            onClick={() => setMobileOpen(false)}
            className="text-lg hover:text-gradientStart flex justify-between items-center"
            activeClassName="text-gradientStart"
          >
            Blogs <FiChevronRight size={18} />
          </NavLink>
        </nav>

        {/* CTA Button */}
        <div className="mt-10">
          {!user ? (
            <Link href="/auth" onClick={() => setMobileOpen(false)}>
              <button className="w-full flex items-center justify-center gap-4 bg-gradientStart text-black font-semibold text-lg px-6 py-4 rounded-lg transition">
                Get Started <FiArrowRight size={22} />
              </button>
            </Link>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-4 bg-gradientStart text-black font-semibold text-lg px-6 py-4 rounded-lg transition"
            >
              Logout
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export default Navbar;
